import { useTranslation } from "react-i18next";
import { MARKET_OPTIONS } from "@vardic/shared/types.ts";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface SettingsPreferencesSectionProps {
  appLanguage: string;
  reportLanguage: string;
  defaultMarket: string;
  prefStatus: string | null;
  onChangeAppLanguage: (lang: string) => void;
  onChangeReportLanguage: (lang: string) => void;
  onChangeDefaultMarket: (market: string) => void;
}

export default function SettingsPreferencesSection({
  appLanguage,
  reportLanguage,
  defaultMarket,
  prefStatus,
  onChangeAppLanguage,
  onChangeReportLanguage,
  onChangeDefaultMarket,
}: SettingsPreferencesSectionProps) {
  const { t } = useTranslation(["settings"]);
  return (
    <section className="space-y-4 rounded-lg border border-[var(--border)] bg-[var(--card)] p-6">
      <div>
        <h2 className="text-lg font-semibold text-[var(--foreground)]">
          {t("settings:preferences.title")}
        </h2>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="app-language" className="block text-sm font-medium text-[var(--foreground)]">
            {t("settings:preferences.appLanguage")}
          </label>
          <p className="mt-1 text-xs text-[var(--muted-foreground)]">
            {t("settings:preferences.appLanguageHint")}
          </p>
          <select
            id="app-language"
            data-test="settings-app-language"
            value={appLanguage}
            onChange={(event) => onChangeAppLanguage(event.target.value)}
            className="mt-2 w-full rounded-[var(--r-sm)] border border-[var(--input)] bg-[var(--background)] px-3 py-2 text-sm text-[var(--foreground)] shadow-sm focus:border-[var(--ring)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--ring)_30%,transparent)]"
          >
            <option value="pl">{t("settings:preferences.languageOptions.pl")}</option>
            <option value="en">{t("settings:preferences.languageOptions.en")}</option>
          </select>
        </div>

        <div>
          <label htmlFor="report-language" className="block text-sm font-medium text-[var(--foreground)]">
            {t("settings:preferences.reportLanguage")}
          </label>
          <p className="mt-1 text-xs text-[var(--muted-foreground)]">
            {t("settings:preferences.reportLanguageHint")}
          </p>
          <select
            id="report-language"
            data-test="settings-report-language"
            value={reportLanguage}
            onChange={(event) => onChangeReportLanguage(event.target.value)}
            className="mt-2 w-full rounded-[var(--r-sm)] border border-[var(--input)] bg-[var(--background)] px-3 py-2 text-sm text-[var(--foreground)] shadow-sm focus:border-[var(--ring)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--ring)_30%,transparent)]"
          >
            <option value="pl">{t("settings:preferences.languageOptions.pl")}</option>
            <option value="en">{t("settings:preferences.languageOptions.en")}</option>
          </select>
        </div>

        <div>
          <label htmlFor="default-market" className="block text-sm font-medium text-[var(--foreground)]">
            {t("settings:preferences.defaultMarket")}
          </label>
          <p className="mt-1 text-xs text-[var(--muted-foreground)]">
            {t("settings:preferences.defaultMarketHint")}
          </p>
          <select
            id="default-market"
            data-test="settings-default-market"
            value={defaultMarket}
            onChange={(event) => onChangeDefaultMarket(event.target.value)}
            className="mt-2 w-full rounded-[var(--r-sm)] border border-[var(--input)] bg-[var(--background)] px-3 py-2 text-sm text-[var(--foreground)] shadow-sm focus:border-[var(--ring)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--ring)_30%,transparent)]"
          >
            {MARKET_OPTIONS.map((market) => (
              <option key={market} value={market}>
                {market}
              </option>
            ))}
          </select>
        </div>
      </div>

      {prefStatus && (
        <Alert variant="success">
          <AlertDescription>{prefStatus}</AlertDescription>
        </Alert>
      )}
    </section>
  );
}
